import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import PrintIcon from "@mui/icons-material/Print";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EventIcon from "@mui/icons-material/Event";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useHistory } from "react-router-dom";
import { listLabelTemplates, deleteLabelTemplate } from "../../services/LabelTemplate";
import { getExportProductById } from "../../services/Export";
import { labelsPerPage, labelPitchMm } from "./labelSchema";
import { RoleBasedRender } from "../../components/RoleBaseRender";
import Ex_ProductSelectingPopup from "../../components/Ex_ProductSelectingPopup";
import FormAlert from "../../components/FormAlert";
import PrintLabelDialog from "./print/PrintLabelDialog";
import ScanLabelDialog from "./print/ScanLabelDialog";

// Roles that may create/edit/delete labels (printing is available to all who see the page).
const CREATE_ROLES = ["ROLE_ADMIN", "LABEL_CREATE"];

const datePart = (v) => (v ? String(v).split(/[ T]/)[0] : "");

/** One-line summary of a saved label's media, for the card. */
const mediaSummary = (layout) => {
  if (!layout || !layout.widthMm) return "—";
  const perPage = labelsPerPage(layout);
  const across = perPage > 1 ? `, ${layout.columns} across` : "";
  return `${layout.widthMm} × ${layout.heightMm} mm${across} · pitch ${labelPitchMm(layout)} mm`;
};

export default function LabelTemplateList() {
  const history = useHistory();
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // New-label flow: pick a product in the popup, press its Done tick, design it.
  const [openProductTable, setOpenProductTable] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const [printTemplate, setPrintTemplate] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await listLabelTemplates();
    setLabels(Array.isArray(rows) ? rows : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNewLabel = () => {
    setAlertData(null);
    setSelectedProduct([]);
    setOpenProductTable(true);
  };

  /**
   * Runs when the popup's Done tick is pressed.
   *
   * SelectingTable reports a selection as an array, and what lands in it depends on
   * where the user clicked: the row handler passes `row[_key]` (the product id) while
   * a cell handler passes the whole row object. Both shapes are handled here, and an
   * id is resolved through the same service the sales screens use.
   */
  const handleProductChosen = async () => {
    const picked = Array.isArray(selectedProduct) ? selectedProduct[0] : selectedProduct;
    setOpenProductTable(false);
    if (picked === undefined || picked === null) return;

    if (typeof picked === "object" && picked._barcode) {
      history.push("/template/label-designer/new", {
        product: {
          productId: picked._productId,
          barcode: picked._barcode,
          productName: picked._productName,
        },
      });
      return;
    }

    setResolving(true);
    const data = await getExportProductById(picked);
    setResolving(false);

    if (data && data !== "Something went wrong..." && data.barcode) {
      history.push("/template/label-designer/new", {
        product: {
          productId: data.product_id,
          barcode: data.barcode,
          productName: data.name,
        },
      });
    } else {
      setAlertData({
        type: "error",
        message: "Could not load that product. Please pick it again.",
      });
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete label "${row.name}"?`)) return;
    const ok = await deleteLabelTemplate(row.id);
    if (ok) load();
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return labels;
    return labels.filter(
      (row) =>
        (row.name || "").toLowerCase().includes(term) ||
        (row.barcode || "").toLowerCase().includes(term) ||
        (row.productName || "").toLowerCase().includes(term)
    );
  }, [labels, search]);

  return (
    <Box className="page">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <QrCode2Icon color="info" />
          <Typography variant="h5" fontWeight={700}>
            Label Printing
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<QrCodeScannerIcon />}
            onClick={() => setScanOpen(true)}
          >
            Scan Label
          </Button>
          <RoleBasedRender allowedRoles={CREATE_ROLES}>
          <Button
            variant="contained"
            startIcon={resolving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={resolving}
            onClick={startNewLabel}
          >
            New Label
          </Button>
          </RoleBasedRender>
        </Stack>
      </Box>

      {alertData && (
        <Box sx={{ mb: 2 }}>
          <FormAlert type={alertData.type} message={alertData.message} />
        </Box>
      )}

      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Design print labels for export products — place a QR code and text on the sticker, then
        print a production run with a serial on every label.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by label, product code or product name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 420, width: "100%" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        <Paper variant="outlined">
          <Box sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
            <Typography>
              {labels.length === 0 ? "No labels yet." : "No labels match that search."}
            </Typography>
            {labels.length === 0 && (
              <Typography variant="body2">Click “New Label” to design one.</Typography>
            )}
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {visible.map((row) => (
            <Grid item xs={12} sm={6} md={4} key={row.id}>
              <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                    <QrCode2Icon color="action" fontSize="small" sx={{ mt: 0.3 }} />
                    <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                      {row.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={row.visibility === "SHARED" ? "Shared" : "Private"}
                      color={row.visibility === "SHARED" ? "success" : "default"}
                      variant="outlined"
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Stack spacing={0.75}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Inventory2OutlinedIcon fontSize="inherit" color="action" />
                      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {row.productName || "Unknown product"}
                      </Typography>
                      <Chip size="small" variant="outlined" label={row.barcode} />
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PersonOutlineIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {row.createdBy || "—"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EventIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        updated {datePart(row.updatedAt) || "—"}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {mediaSummary(row.layout)}
                    </Typography>
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={() => setPrintTemplate(row)}
                    sx={{ mr: "auto" }}
                  >
                    Print
                  </Button>
                  <RoleBasedRender allowedRoles={CREATE_ROLES}>
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => history.push("/template/label-designer/edit/" + row.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDelete(row)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  </RoleBasedRender>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {openProductTable && (
        <Ex_ProductSelectingPopup
          openProductTable={openProductTable}
          setOpenProductTable={setOpenProductTable}
          // The popup's own "Done" tick calls this once a product is picked.
          handleCloseProductTable={handleProductChosen}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          isOneChoise={true}
        />
      )}

      <ScanLabelDialog open={scanOpen} onClose={() => setScanOpen(false)} />

      <PrintLabelDialog
        open={Boolean(printTemplate)}
        onClose={() => setPrintTemplate(null)}
        template={printTemplate}
      />
    </Box>
  );
}
